import {
  Avatar,
  Breadcrumb,
  Button,
  Col,
  Descriptions,
  Form,
  FormInstance,
  Image,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Row,
  Select,
  Table,
  Tag,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { IPo, IPoApi, IPoItem } from "common/interface";
import { getDateFromISODate } from "common/utils";
import { variables } from "common/variables";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { RiAddBoxLine } from "react-icons/ri";
import {
  getPoById,
  getRtvReasons,
  updateGrossReceivedQty,
  updateB2cSortingRtv,
} from "store/po/poSlice";
import { fallBackImage } from "common/styles";

const MESSAGE_KEY = "po_receieve";
type PartialIPoItemType = Partial<IPoItem> & { key: string };
type Page = "sorting-rtv" | "receiving";

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "select" | "text";
  record: PartialIPoItemType;
  index: number;
  children: React.ReactNode;
  cellForm: FormInstance;
  getSkuOptions: (includeSku?: string) => Record<string, { skuCode: string; skuName: string; value: any; label: string }>;
  isRowExpanded: boolean;
  onUnsavedChange: (id: number | undefined) => void;
  onConfirmSave?: (dataIndex: string, record: any, newValue: number, extra?: Record<string, any>) => Promise<void>;
  rtvReasons?: string[];
}

const { Option } = Select;

const headerKeys: { [key: string]: string } = {
  skuId: "SKU ID/Name",
  uom: "UOM",
  // extPoIds: "Ext PO ID",
  requestedQty: "Requested Quantity",
  receivedImages: "Received Images",
  rtvAllowed: "RTV Allowed",
  grossReceivedQty: "Gross Rcv. Qty.",
  sortingRtv: "Sorting RTV",
  rejectedQty: "Rejected Qty.",
  // netReceivedQty: "Net Rcv. Qty.",
  finalReceivedQty: "Final Received Qty.",
  // poRate: "PO Rate",
  // billingRate: "Billing Rate",
  // totalAmount: "Total Amount",
  actions: "Actions",
};

// Defined outside the component so its reference is stable across renders.
// If defined inside, React treats it as a new component type every render and
// unmounts/remounts every table cell, which is the primary cause of slowness.
const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  cellForm,
  getSkuOptions,
  isRowExpanded,
  onUnsavedChange,
  onConfirmSave,
  rtvReasons,
  ...restProps
}) => {
  // requestedQty always shows the raw value, never an input
  if (dataIndex === "requestedQty") {
    return <td className={"ant-table-cell"}>{record.requestedQty}</td>;
  }

  // receivedImages is display-only, never an input
  if (dataIndex === "receivedImages") {
    return <td {...restProps}>{children}</td>;
  }

  const isFieldEditable =
    (["grossReceivedQty", "finalReceivedQty", "rejectedQty"].includes(dataIndex)) ||
    (dataIndex === "sortingRtv" && record.rtvAllowed);

  // Not in edit mode for this cell — render the display value and stop here.
  // No input nodes, no getSkuOptions call, no Popconfirm created.
  if (!(editing && isFieldEditable)) {
    return <td {...restProps}>{children}</td>;
  }

  // --- Everything below runs only when this specific cell is being edited ---

  const isFieldTypeAddition =
    dataIndex === "grossReceivedQty" ||
    dataIndex === "sortingRtv" ||
    dataIndex === "rejectedQty";
  const isFieldBoolean = dataIndex === "rtvAllowed";

  let inputNode: React.ReactNode;

  if (isFieldTypeAddition) {
    inputNode = (
      <Input
        key={dataIndex}
        readOnly={true}
        onChange={() => onUnsavedChange(record.id)}
        type={"text"}
        disabled={
          (dataIndex === "sortingRtv" && !record.rtvAllowed) ||
          (dataIndex === "rejectedQty" && record.rtvAllowed)
        }
        suffix={
          <Popconfirm
            title={
              <>
                <p>Enter value to add</p>
                <Form.Item
                  name={"tempAddField"}
                  rules={[{ required: isRowExpanded, message: `Please input value!` }]}
                >
                  <InputNumber
                    onChange={async () => { onUnsavedChange(record.id); }}
                    min={
                      0 -
                      parseFloat(
                        (cellForm.getFieldValue(dataIndex)
                          ? cellForm.getFieldValue(dataIndex)
                          : 0
                        )?.toString()
                      )
                    }
                  />
                </Form.Item>
                {dataIndex === "sortingRtv" && (
                  <Form.Item
                    name={"rtvReasonField"}
                    rules={[{ required: isRowExpanded, message: "Please select RTV reason!" }]}
                    style={{ marginTop: 8 }}
                  >
                    <Select placeholder="Select RTV Reason" style={{ minWidth: 180 }}>
                      {(rtvReasons ?? []).map((reason) => (
                        <Option key={reason} value={reason}>{reason}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </>
            }
            placement="topRight"
            onConfirm={async () => {
              const fieldsToValidate: string[] = ["tempAddField"];
              if (dataIndex === "sortingRtv") fieldsToValidate.push("rtvReasonField");
              await cellForm.validateFields(fieldsToValidate);
              const addValue = parseFloat(cellForm.getFieldValue("tempAddField"));
              const currentValue = parseFloat(
                (cellForm.getFieldValue(dataIndex)
                  ? cellForm.getFieldValue(dataIndex)
                  : 0
                )?.toString()
              );
              const newValue = addValue + currentValue;
              cellForm.setFieldsValue({ [dataIndex]: newValue, tempAddField: "0" });
              if (dataIndex === "grossReceivedQty" || dataIndex === "sortingRtv") {
                const extra = dataIndex === "sortingRtv"
                  ? { rtvReason: cellForm.getFieldValue("rtvReasonField") }
                  : undefined;
                await onConfirmSave?.(dataIndex, record, addValue, extra);
              }
            }}
            onCancel={() => {
              cellForm.setFieldsValue({ tempAddField: "0", rtvReasonField: undefined });
            }}
            onVisibleChange={() => {
              cellForm.setFieldsValue({ tempAddField: "0", rtvReasonField: undefined });
            }}
          >
            <RiAddBoxLine size={20} />
          </Popconfirm>
        }
      />
    );
  } else if (inputType === "select") {
    inputNode = (
      <Select
        key={dataIndex}
        showSearch
        optionFilterProp=""
        filterOption={(input, option) => {
          const keyWords =
            option?.children?.map((child: any) =>
              child.props.children.toLowerCase()
            ) || [];
          return keyWords.some((key: string) => key.includes(input.toLowerCase()));
        }}
        style={!isFieldBoolean ? { width: "200px" } : {}}
      >
        {!isFieldBoolean
          ? Object.entries(getSkuOptions(record.skuCode)).map(([key, value]) => (
              <Option key={key} value={key}>
                <div>{value.skuCode}</div>
                <div>{value.skuName}</div>
              </Option>
            ))
          : (
            <>
              <Option key={`${dataIndex}_option_1`} value={true}><div>Yes</div></Option>
              <Option key={`${dataIndex}_option_2`} value={false}><div>No</div></Option>
            </>
          )}
      </Select>
    );
  } else {
    inputNode = (
      <Input
        key={dataIndex}
        readOnly={dataIndex === "finalReceivedQty"}
        type={"text"}
        onChange={() => onUnsavedChange(record.id)}
        disabled={
          (dataIndex === "sortingRtv" && !record.rtvAllowed)
        }
      />
    );
  }

  return (
    <td {...restProps}>
      <Form.Item
        name={dataIndex}
        style={{ margin: 0 }}
        rules={[
          {
            required: dataIndex === "grossReceivedQty",
            message: `Please ${inputType === "select" ? "select" : "input"} ${title}!`,
          },
        ]}
      >
        {inputNode}
      </Form.Item>
    </td>
  );
};

const POReceivePage = () => {
  const dispatch = useAppDispatch();
  const { pos, error, status, rtvReasons } = useAppSelector((state) => state.po);
  const { masterSkus = [], assets = [] } = useAppSelector(
    (state) => state.requisition
  );
  const { poId } = useParams();
  const [viewedPO, setViewedPO] = useState<IPo>();
  const [tableRows, setTableRows] = useState<any[]>();
  const [tempTableRows, setTempTableRows] = useState<any[]>();
  const [form] = Form.useForm();
  const [assetForm] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isRowExpanded, setIsRowExpanded] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<number | undefined>(undefined);
  const [localPos, setLocalPos] = useState<IPoApi>();
  const [hasUnsavedItems, setHasUnsavedItems] = useState<number | undefined>(undefined);
  const [page, setPage] = useState<Page>("sorting-rtv");
  const navigate = useNavigate();
  const location = useLocation();
  const [hasFetched, setHasFetched] = useState(false);

  // Stable table components object — never recreated, prevents cell remounts
  const tableComponents = useMemo(
    () => ({ body: { cell: EditableCell } }),
    []
  );

  useEffect(() => {
    const path = location.pathname.split("/").pop();
    if (path === "sorting-rtv") {
      setPage("sorting-rtv");
    } else {
      setPage("receiving");
    }
  }, [location]);

  useEffect(() => {
    if (editingKey) {
      const selectedRowData = viewedPORef.current?.poItems.find(
        (e) => e.id.toString() === editingKey.toString().split("-")[0]
      );
      form.setFieldsValue({
        skuId: masterSkus
          .find((k) => k.skuCode === selectedRowData?.skuCode)
          ?.id.toString(),
      });
    }
  }, [editingKey, form, masterSkus]);

  useEffect(() => {
    if (hasUnsavedItems) {
      message.warning({
        content: (
          <div style={{ width: "100%" }}>
            <p>
              There are unsaved items, please click <b>Done</b>.
            </p>
          </div>
        ),
        key: MESSAGE_KEY,
        duration: 0,
      });
    } else {
      message.destroy();
    }
  }, [hasUnsavedItems]);

  const isEditing = useCallback(
    (record: PartialIPoItemType) => record.key === editingKey,
    [editingKey]
  );

  const edit = useCallback(
    (record: PartialIPoItemType) => {
      form.setFieldsValue({ ...record });
      setEditingKey(record.key);
    },
    [form]
  );

  // Refs so callbacks always read the latest values without being listed as
  // deps — prevents column/row memo invalidation on every localPos change.
  const localPosRef = useRef(localPos);
  localPosRef.current = localPos;
  const viewedPORef = useRef(viewedPO);
  viewedPORef.current = viewedPO;
  const editingKeyRef = useRef(editingKey);
  editingKeyRef.current = editingKey;
  const assetsRef = useRef(assets);
  assetsRef.current = assets;
  const isRowExpandedRef = useRef(isRowExpanded);
  isRowExpandedRef.current = isRowExpanded;
  const pageRef = useRef(page);
  pageRef.current = page;

  // Defined before saveItem so the reference is available in saveItem's closure.
  const calculateAndUpdateNetRcvQty = useCallback(() => {
    const {
      grossReceivedQty = 0,
      sortingRtv = 0,
      rejectedQty = 0,
    } = form.getFieldsValue([
      "grossReceivedQty",
      "sortingRtv",
      "rejectedQty",
    ]);
    const viewedPO = viewedPORef.current;
    const editingKey = editingKeyRef.current;
    const assets = assetsRef.current;
    const thisItem = viewedPO?.poItems.find(
      (po) => po.id.toString() === editingKey.toString()
    );
    if (thisItem) {
      const selectedAssets = thisItem.poItemAssets ?? [];
      const selectedAssetsQty = selectedAssets.reduce(
        (total, selectedAsset) => {
          const assetWeight =
            assets.find((a) => a.id === selectedAsset.assetId)?.assetWeight ?? 0;
          total += selectedAsset.assetQty * assetWeight;
          return total;
        },
        0
      );
      const diviser = thisItem.uom.toLowerCase() === "kg" ? 1000 : 1;
      const totalAssetQty = (selectedAssetsQty ?? 0) / diviser;
      const finalQty =
        Number(grossReceivedQty ?? 0) -
        Number(sortingRtv ?? 0) -
        Number(rejectedQty ?? 0) -
        (thisItem.uom.toLowerCase() === "pieces" ? 0 : totalAssetQty);

      form.setFieldsValue({
        finalReceivedQty: finalQty >= 0 ? finalQty : 0,
      });
    } else {
      form.setFieldsValue({ finalReceivedQty: 0 });
    }
    // All volatile values (assets, editingKey, viewedPO) read from refs
  }, [form]);

  const saveItem = useCallback(
    async (record: any, closeEdit = false) => {
      try {
        calculateAndUpdateNetRcvQty();
        const row = await form.validateFields();
        const viewedPO = viewedPORef.current;
        const localPos = localPosRef.current;
        const isRowExpanded = isRowExpandedRef.current;

        const thisItemIdx =
          viewedPO?.poItems.findIndex(
            (po) => po.id.toString() === record.id.toString()
          ) ?? -1;
        if (thisItemIdx > -1) {
          const viewedPoIndex =
            localPos?.data.findIndex(
              (d) => d.id.toString() === viewedPO?.id.toString()
            ) ?? -1;

          if (viewedPoIndex > -1) {
            let newLocalPos = Object.assign({}, localPos);
            newLocalPos.data = (localPos?.data || []).slice();
            newLocalPos.data[viewedPoIndex] = {
              ...newLocalPos.data[viewedPoIndex],
              poItems: [...(localPos?.data[viewedPoIndex].poItems || [])],
            };

            newLocalPos.data[viewedPoIndex].poItems[thisItemIdx] = {
              ...newLocalPos.data[viewedPoIndex].poItems[thisItemIdx],
              ...row,
            };
            setLocalPos(newLocalPos as any);
            (closeEdit || !isRowExpanded) && setEditingKey("");
            if (closeEdit) {
              setIsRowExpanded(false);
              setExpandedRowId(undefined);
            }
            setHasUnsavedItems(undefined);
          }
          form.setFieldsValue({ tempAddField: "0" });
        }
      } catch (e) {}
    },
    // All volatile values read from refs; calculateAndUpdateNetRcvQty is stable ([form])
    [form, calculateAndUpdateNetRcvQty]
  );

  const onAddAsset = useCallback(
    async (record: any) => {
      try {
        const assetRow = await assetForm.validateFields();
        const viewedPO = viewedPORef.current;
        const localPos = localPosRef.current;

        const thisItemIdx =
          viewedPO?.poItems.findIndex(
            (po) => po.id.toString() === record.id.toString()
          ) ?? -1;
        if (thisItemIdx > -1) {
          const viewedPoIndex =
            localPos?.data.findIndex(
              (d) => d.id.toString() === viewedPO?.id.toString()
            ) ?? -1;

          if (viewedPoIndex > -1) {
            let newLocalPos = Object.assign({}, localPos);
            newLocalPos.data = (localPos?.data || []).slice();
            newLocalPos.data[viewedPoIndex] = {
              ...newLocalPos.data[viewedPoIndex],
              poItems: [...(localPos?.data[viewedPoIndex].poItems || [])],
            };

            newLocalPos.data[viewedPoIndex].poItems[thisItemIdx] = {
              ...newLocalPos.data[viewedPoIndex].poItems[thisItemIdx],
            };

            newLocalPos.data[viewedPoIndex].poItems = [
              ...newLocalPos.data[viewedPoIndex].poItems,
            ];

            const existingAssetIdx =
              newLocalPos.data[viewedPoIndex].poItems[
                thisItemIdx
              ].poItemAssets?.findIndex((a) => a.assetId === assetRow.id) ?? -1;

            if (existingAssetIdx > -1) {
              newLocalPos.data[viewedPoIndex].poItems[
                thisItemIdx
              ].poItemAssets = [
                ...newLocalPos.data[viewedPoIndex].poItems[thisItemIdx]
                  .poItemAssets,
              ];
              newLocalPos.data[viewedPoIndex].poItems[thisItemIdx].poItemAssets[
                existingAssetIdx
              ] = {
                assetId: assetRow.id,
                assetQty: assetRow.assetQty,
              };
            } else {
              newLocalPos.data[viewedPoIndex].poItems[
                thisItemIdx
              ].poItemAssets = [
                ...((newLocalPos.data[viewedPoIndex].poItems[thisItemIdx] ?? [])
                  .poItemAssets ?? []),
                {
                  assetId: assetRow.id,
                  assetQty: assetRow.assetQty,
                },
              ];
            }

            setLocalPos(newLocalPos as any);
            assetForm.resetFields();
            setHasUnsavedItems(record.id);
          }
        }
      } catch (e) {}
    },
    [assetForm]
  );

  useEffect(() => {
    if (poId) {
      dispatch(getPoById(poId)).then(() => setHasFetched(true));
    }
    dispatch(getRtvReasons());
  }, [poId, dispatch]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "loading purchase order", key: MESSAGE_KEY });
    } else if (status === "saving") {
      message.loading({ content: "saving purchase order", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [status, error]);

  // Sync localPos from Redux when PO data arrives; navigate away if not found
  useEffect(() => {
    const thisPO = pos?.data.find((p) => p.id.toString() === poId);
    if (thisPO) {
      setViewedPO(thisPO);
      setLocalPos(pos);
    } else if (hasFetched) {
      navigate("/po/all");
    }
  }, [navigate, poId, pos, hasFetched]);

  // Rebuild tableRows when local data changes (separate from column computation)
  useEffect(() => {
    const thisPO = localPos?.data?.find((po) => po.id.toString() === poId);
    if (!thisPO) return;

    const rows: any[] =
      thisPO?.poItems
        ?.map((poItem) => ({
          id: poItem.id,
          skuId: poItem.id,
          key: poItem.id,
          skuCode: poItem.skuCode,
          skuName: poItem.skuName,
          extPoIds: poItem.extPoIds,
          requestedQty: poItem.requestedQty,
          receivedImageUrls: poItem.receivedImageUrls ?? [],
          grossReceivedQty: poItem.grossReceivedQty,
          rtvAllowed: poItem.rtvAllowed,
          sortingRtv: poItem.sortingRtv,
          rejectedQty: poItem.rejectedQty,
          uom: poItem.uom,
          poRate: poItem.poRate,
          billingRate: poItem.billingRate,
          totalAmount: poItem.totalAmount,
          finalReceivedQty: poItem.finalReceivedQty,
          poItemAssets: poItem.poItemAssets,
        }))
        .filter((poI) => (isAddingItem && !(poI as any).temp) || !isAddingItem) ||
      [];

    setViewedPO(thisPO);
    setTableRows([...rows, ...(tempTableRows || [])]);
  }, [localPos?.data, poId, isAddingItem, tempTableRows]);

  const getSkuOptions = useCallback(
    (includeSku?: string) => {
      if (masterSkus) {
        const selectedSkus = viewedPORef.current?.poItems
          .filter((poi) => poi.skuCode !== includeSku)
          .map((poI) => poI.skuCode);
        return masterSkus.reduce((acc, sku) => {
          if (!selectedSkus?.includes(sku.skuCode)) {
            return (acc = {
              ...acc,
              [sku.id]: {
                value: sku.id,
                label: sku.skuCode,
                skuCode: sku.skuCode,
                skuName: sku.name,
              },
            });
          }
          return acc;
        }, {} as Record<string, { skuCode: string; skuName: string; value: any; label: string }>);
      }
      return {};
    },
    [masterSkus]
  );

  const handleFieldConfirmSave = useCallback(
    async (dataIndex: string, record: any, newValue: number, extra?: Record<string, any>) => {
      const viewedPO = viewedPORef.current;
      const numericPoId = viewedPO?.id ? Number(viewedPO.id) : undefined;
      if (!numericPoId) return;
      try {
        if (dataIndex === "grossReceivedQty") {
          await dispatch(updateGrossReceivedQty({
            poId: numericPoId,
            poItems: [{ poItemId: record.id, receivedQty: newValue }],
          })).unwrap();
        } else if (dataIndex === "sortingRtv") {
          await dispatch(updateB2cSortingRtv({
            poId: numericPoId,
            itemId: record.id,
            sortingRtv: newValue,
            rtvReason: extra?.rtvReason ?? "",
          })).unwrap();
        }
      } catch (e) {}
    },
    [dispatch]
  );

  const removeAsset = useCallback(
    (poItemId: number, assetId: number) => {
      const localPos = localPosRef.current;
      const viewedPO = viewedPORef.current;
      const thisPoIndex =
        localPos?.data.findIndex((po) => po.id === viewedPO?.id) ?? -1;

      if (thisPoIndex > -1) {
        const newLocalPos = {
          ...localPos,
          data: [...(localPos?.data ?? [])],
        } as IPoApi;

        newLocalPos.data[thisPoIndex] = {
          ...newLocalPos.data[thisPoIndex],
        };

        newLocalPos.data[thisPoIndex].poItems = [
          ...newLocalPos.data[thisPoIndex].poItems,
        ];

        const thisPo = { ...localPos?.data[thisPoIndex] };
        const newPoItems = [...(thisPo.poItems ?? [])];
        const poItemIdx = newPoItems.findIndex((p) => p.id === poItemId);
        const newPoAssets = [
          ...newPoItems[poItemIdx].poItemAssets.filter(
            (poiA) => poiA.assetId !== assetId
          ),
        ];
        newPoItems[poItemIdx] = {
          ...newPoItems[poItemIdx],
          poItemAssets: newPoAssets,
        };

        thisPo.poItems = newPoItems;
        newLocalPos.data[thisPoIndex] = thisPo as IPo;

        setLocalPos(newLocalPos);
        setHasUnsavedItems(poItemId);
      }
    },
    []
  );

  const getSelectedAssets = useCallback(
    (record: any) => {
      const { id } = record;
      const localPos = localPosRef.current;
      const viewedPO = viewedPORef.current;
      const thisPo = localPos?.data.find((p) => p.id === viewedPO?.id);
      const previousPoItems = [
        ...(pos?.data.find((po) => po.id.toString() === poId)?.poItems ?? []),
      ];

      if (thisPo) {
        const thisPoItem = (
          (hasUnsavedItems && isRowExpanded) || isRowExpanded
            ? thisPo.poItems
            : previousPoItems
        ).find((poItem) => poItem.id === id);

        if (thisPoItem) {
          const selectedAssetIds = (thisPoItem.poItemAssets ?? []).map(
            (asset) => ({ id: asset.assetId, qty: asset.assetQty })
          );
          const selectedAssets = selectedAssetIds.map((selectedAsset) => ({
            asset: assets.find((asset) => asset.id === selectedAsset.id),
            qty: selectedAsset.qty,
          }));
          return (
            <>
              {selectedAssets.map((asset, i) => (
                <Tag
                  closable={page !== "sorting-rtv"}
                  key={asset?.asset?.id ?? i}
                  onClose={() => removeAsset(record.id, asset!.asset!.id)}
                >
                  {asset.asset?.assetName} x {asset.qty}
                </Tag>
              ))}
            </>
          );
        }
      }

      return <></>;
    },
    [assets, hasUnsavedItems, isRowExpanded, poId, pos?.data, removeAsset, page]
  );

  // Column definitions — recompute only when editing state or display mode changes,
  // not on every data update.
  const tableColumns = useMemo<ColumnsType<PartialIPoItemType>>(() => {
    const columns = [
      ...Object.keys(headerKeys).map((key) => ({
        key,
        title: headerKeys[key],
        dataIndex: key,
        editable: key !== "actions",
        render: (text: string, record: any) => {
          if (key === "skuId") {
            return (
              <>
                <div>{record.skuCode}</div>
                <div>{record.skuName}</div>
              </>
            );
          }

          if (key === "extPoIds") {
            return ((record.extPoIds as string[]) || []).map((exPoId) => (
              <div key={record.challanId + exPoId}>{exPoId}</div>
            ));
          }

          if (key === "receivedImages") {
            return (
              <Row align="middle" gutter={[4, 4]}>
                {(record.receivedImageUrls ?? []).map((image: string, idx: number) => (
                  <Col key={idx}>
                    <Avatar
                      src={
                        <Image
                          src={image}
                          fallback={fallBackImage}
                          alt="Received"
                          width="100%"
                        />
                      }
                    />
                  </Col>
                ))}
              </Row>
            );
          }

          if (key === "rtvAllowed") {
            if (record.rtvAllowed === undefined) {
              return <div></div>;
            } else {
              return <div>{record.rtvAllowed ? "Yes" : "No"}</div>;
            }
          }

          if (key === "") {
            return ((record.request as string[]) || []).map((exPoId) => (
              <div key={record.challanId + exPoId}>{exPoId}</div>
            ));
          }

          if (key === "actions") {
            return isEditing(record) ? (
              <Button type="primary" size="small" onClick={() => saveItem(record, true)}>
                Done
              </Button>
            ) : <></>;
          }

          return <>{text}</>;
        },
      })),
    ];

    const mergedColumns = columns.map((col, i) => {
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        ...(i === 0 && { width: "220px" }),
        onCell: (record: PartialIPoItemType) => ({
          record,
          inputType: ["skuId", "rtvAllowed"].includes(col.dataIndex)
            ? "select"
            : "text",
          dataIndex: col.dataIndex,
          title: col.title,
          editing: isEditing(record),
          cellForm: form,
          getSkuOptions,
          isRowExpanded,
          onUnsavedChange: setHasUnsavedItems,
          onConfirmSave: handleFieldConfirmSave,
          rtvReasons,
        }),
      };
    });

    return mergedColumns;
  }, [isEditing, isRowExpanded, saveItem, form, getSkuOptions, handleFieldConfirmSave, rtvReasons]);

  const poInfo = viewedPO && (
    <>
      <Descriptions
        size="small"
        layout="vertical"
        labelStyle={{ fontWeight: "bold" }}
        bordered
        column={{ xs: 2, md: 4 }}
      >
        <Descriptions.Item label="PO ID">{viewedPO.poId}</Descriptions.Item>
        <Descriptions.Item label="Status">{viewedPO.status}</Descriptions.Item>
        <Descriptions.Item label="Expected Delivery Date">
          {getDateFromISODate(viewedPO.expectedDeliveryDate)}
        </Descriptions.Item>
      </Descriptions>
      {viewedPO.vendorDetails && (
        <Descriptions
          size="small"
          layout="vertical"
          labelStyle={{ fontWeight: "bold" }}
          bordered
          column={{ xs: 2, md: 3 }}
          style={{ marginTop: "20px" }}
        >
          <Descriptions.Item label="Vendor Detail">
            {viewedPO.vendorDetails?.vendorCode} ||{" "}
            {viewedPO.vendorDetails?.name}
          </Descriptions.Item>
        </Descriptions>
      )}
      {!!(tableRows && tableRows.length) && (
        <Row style={{ marginTop: "20px" }}>
          <Col xs={24}>
            <Form form={form} component={false}>
              <Table
                className="po-item-table"
                components={tableComponents}
                bordered
                dataSource={tableRows}
                columns={tableColumns}
                rowClassName={(_record, index) =>
                  index === tableRows.length - 1
                    ? "editable-last-row editable-row"
                    : "editable-row"
                }
                pagination={false}
                scroll={{ x: "90%", y: "65vh" }}
                style={{ width: "100%" }}
                size="small"
                expandable={{
                  onExpand: (isExpanded, record) => {
                    setIsRowExpanded(isExpanded);
                    if (isExpanded) {
                      setExpandedRowId(record.id);
                      edit(record);
                    } else {
                      setExpandedRowId(undefined);
                      setEditingKey("");
                      setTempTableRows([]);
                      setIsAddingItem(false);

                      form.resetFields([
                        "requestedQty",
                        "grossReceivedQty",
                        "finalReceivedQty",
                        "rtvReasonField",
                      ]);
                      assetForm.resetFields();
                    }
                  },
                  expandedRowKeys: [expandedRowId ? expandedRowId : ""],
                  rowExpandable: (record) =>
                    record.id === expandedRowId ||
                    (!expandedRowId && record.id === hasUnsavedItems) ||
                    (!expandedRowId && !hasUnsavedItems),
                  expandedRowRender: (record) => {
                    if (record?.uom?.toLowerCase() === "xyz") {
                      return (
                        <>
                          <div>{getSelectedAssets(record)}</div>
                          {page !== "sorting-rtv" && (
                            <div>
                              {isRowExpanded && (
                                <div
                                  style={{ padding: "20px 0", width: "100%" }}
                                >
                                  <Form
                                    form={assetForm}
                                    layout="inline"
                                    style={{ width: "100%" }}
                                    component={false}
                                  >
                                    <Row style={{ width: "100%" }}>
                                      <Col
                                        xs={24}
                                        md={12}
                                        xl={6}
                                        style={{ marginRight: "20px" }}
                                      >
                                        <Form.Item
                                          name="id"
                                          label="Asset"
                                          rules={[
                                            {
                                              required: true,
                                              message: "Please select Asset",
                                            },
                                          ]}
                                        >
                                          <Select>
                                            {assets.map((asset) => (
                                              <Option
                                                value={asset.id}
                                                key={asset.id}
                                              >{`${asset.assetName} (${asset.assetWeight})`}</Option>
                                            ))}
                                          </Select>
                                        </Form.Item>
                                      </Col>
                                      <Col xs={24} md={12} xl={6}>
                                        <Form.Item
                                          name="assetQty"
                                          label="Asset Qty"
                                          rules={[
                                            {
                                              required: true,
                                              message: "Please enter Asset Qty",
                                            },
                                          ]}
                                        >
                                          <Input type="number" />
                                        </Form.Item>
                                      </Col>
                                    </Row>
                                    <Row>
                                      <Col>
                                        <Button
                                          onClick={() => onAddAsset(record)}
                                          type="primary"
                                          size="small"
                                        >
                                          Add Asset
                                        </Button>
                                      </Col>
                                    </Row>
                                  </Form>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      );
                    }
                  },
                }}
              />
            </Form>
          </Col>
        </Row>
      )}
    </>
  );

  useDocumentTitle(
    page === "sorting-rtv"
      ? "Update Purchase Order GRN"
      : "Receive Purchase Order GRN"
  );

  return (
    <>
      <Row
        style={{
          margin: "1rem 0",
          position: "sticky",
          top: 0,
          zIndex: 1,
          background: variables.wmBackgroundColorLight1,
          padding: "5px",
        }}
      >
        <Col xs={24}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <NavLink to="/po/all">Purchase Orders</NavLink>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <NavLink to={`/po/${viewedPO?.id}`}>
                {viewedPO?.vendorDetails?.name}
              </NavLink>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {page === "sorting-rtv" ? "Update" : "Receive"}
            </Breadcrumb.Item>
          </Breadcrumb>
        </Col>
      </Row>
      <Row>
        <Col>
          <AppPageHeader
            title={`${
              page === "sorting-rtv" ? "Update" : "Receive"
            } Purchase Order GRN`}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={24}>{poInfo}</Col>
      </Row>
    </>
  );
};

export default POReceivePage;
